import React, { useEffect, useState } from "react";
import {
  Card,
  EmptyState,
  Icon,
  IconButton,
  Select,
  styles as ui,
} from "stories/Admin";
import { Poster, TvAlertKey, TvAlertMinutes } from "types/TvPosterTypes";
import {
  ALERT_FIELDS,
  ALERT_MINUTE_OPTIONS,
  DEFAULT_ALERT_MINUTES,
  carouselImage,
  reorder,
  slideOptions,
} from "./tvOrder";
import styles from "./TvOrderPanel.module.scss";

/*
 * «Rekkefølge på TV»: plakatene med «Vis på TV» i den rekkefølgen TV-en viser
 * dem (dra og slipp, eller pilene), og en liten forhåndsvisning av
 * Standard-visningen som blar gjennom dem med valgt tid per plakat.
 */

/** Eksempel på bønnetider i forhåndsvisningen (salah, jamat). */
const SAMPLE_PRAYERS: Array<[string, string, string]> = [
  ["Fajr", "06:12", "06:30"],
  ["Dhuhr", "12:58", "13:15"],
  ["Asr", "15:22", "15:40"],
  ["Maghrib", "17:51", "17:56"],
  ["Isha", "19:38", "19:50"],
];

const osloClock = (at: Date) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Oslo",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** Indeks til neste bønn (etter klokken), eller Fajr etter Isha. */
const nextPrayer = (clock: string) => {
  const now = toMinutes(clock);
  const index = SAMPLE_PRAYERS.findIndex(([, salah]) => toMinutes(salah) > now);
  return index < 0 ? 0 : index;
};

interface TvPreviewProps {
  posters: Poster[];
  slideSeconds: number;
}

/** 16:9 TV-ramme som etterligner Standard-visningen. */
export const TvPreview: React.FC<TvPreviewProps> = ({
  posters,
  slideSeconds,
}) => {
  const [index, setIndex] = useState(0);
  const [clock, setClock] = useState(() => osloClock(new Date()));
  const count = posters.length;
  const key = posters.map((p) => p.id).join(",");

  // Start forfra når listen eller tiden endres.
  useEffect(() => setIndex(0), [key, slideSeconds]);

  useEffect(() => {
    if (count < 2) return;
    const timer = window.setInterval(
      () => setIndex((i) => (i + 1) % count),
      slideSeconds * 1000,
    );
    return () => window.clearInterval(timer);
  }, [count, slideSeconds, key]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setClock(osloClock(new Date())),
      15_000,
    );
    return () => window.clearInterval(timer);
  }, []);

  const current = posters[Math.min(index, count - 1)];
  const next = nextPrayer(clock);

  return (
    <figure className={styles.preview}>
      <div
        className={styles.tv}
        role="img"
        aria-label={
          current
            ? `Forhåndsvisning av TV-en: plakat ${index + 1} av ${count}, «${current.tittel}»`
            : "Forhåndsvisning av TV-en uten plakater"
        }
      >
        <div className={styles.tvLeft}>
          <span className={styles.tvClock}>{clock}</span>
          <ul className={styles.tvPrayers}>
            {SAMPLE_PRAYERS.map(([name, salah, jamat], i) => (
              <li key={name} className={i === next ? styles.tvNext : undefined}>
                <span>{name}</span>
                <span>{salah}</span>
                <span>{jamat}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.tvRight}>
          {current ? (
            <img
              key={current.id}
              src={carouselImage(current)}
              alt=""
              className={styles.tvPoster}
            />
          ) : (
            <span className={styles.tvEmpty}>Ingen plakater på TV</span>
          )}
          {count > 1 && (
            <span className={styles.tvDots} aria-hidden="true">
              {posters.map((p, i) => (
                <i key={p.id} className={i === index ? styles.on : undefined} />
              ))}
            </span>
          )}
        </div>
      </div>
      <figcaption className={styles.caption}>
        {count > 0 ? (
          <>
            <b>
              {index + 1} / {count}
            </b>
            <span className={styles.captionTitle}>{current?.tittel}</span>
            <span>{slideSeconds} s per plakat</span>
          </>
        ) : (
          <span>Slå på «Vis på TV» for å se plakatene her.</span>
        )}
      </figcaption>
      <span className={styles.note}>
        Bønnetidene er et eksempel. TV-en viser moskeens egne tider.
      </span>
    </figure>
  );
};

interface TvOrderPanelProps {
  /** Plakatene med «Vis på TV», i rekkefølge. */
  posters: Poster[];
  /** Ny rekkefølge (id-er) for TV-plakatene. */
  onReorder: (ids: number[]) => void;
  slideSeconds: number;
  onSlideSecondsChange: (seconds: number) => void;
  savingSeconds?: boolean;
  /** «Varselskjermer på TV»: minutter for adhan, fellesbønn og jummah. */
  alertMinutes: TvAlertMinutes;
  onAlertMinutesChange: (key: TvAlertKey, minutes: number) => void;
  savingAlerts?: boolean;
  loading?: boolean;
}

interface TvAlertSettingsProps {
  value: TvAlertMinutes;
  onChange: (key: TvAlertKey, minutes: number) => void;
  disabled?: boolean;
}

/** «Varselskjermer på TV»: hvor lenge adhan-, fellesbønn- og jummah-skjermen står. */
export const TvAlertSettings: React.FC<TvAlertSettingsProps> = ({
  value,
  onChange,
  disabled,
}) => (
  <section className={styles.alerts} aria-labelledby="tv-alerts-title">
    <div className={styles.alertsHead}>
      <h3 id="tv-alerts-title" className={styles.alertsTitle}>
        Varselskjermer på TV
      </h3>
      <p className={styles.alertsText}>
        Hvor lenge TV-en viser skjermen ved adhan, fellesbønn og jummah før den
        går tilbake til vanlig visning.
      </p>
    </div>
    <div className={styles.alertsFields}>
      {ALERT_FIELDS.map(({ key, label }) => (
        <Select
          key={key}
          label={label}
          help={`Standard: ${DEFAULT_ALERT_MINUTES[key]} min`}
          value={value[key]}
          disabled={disabled}
          onChange={(e) => onChange(key, Number(e.target.value))}
        >
          {ALERT_MINUTE_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m} min
            </option>
          ))}
        </Select>
      ))}
    </div>
  </section>
);

const TvOrderPanel: React.FC<TvOrderPanelProps> = ({
  posters,
  onReorder,
  slideSeconds,
  onSlideSecondsChange,
  savingSeconds,
  alertMinutes,
  onAlertMinutesChange,
  savingAlerts,
  loading,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [announce, setAnnounce] = useState("");

  const move = (from: number, to: number) => {
    if (to < 0 || to >= posters.length || from === to) return;
    onReorder(
      reorder(
        posters.map((p) => p.id),
        from,
        to,
      ),
    );
    setAnnounce(`«${posters[from].tittel}» er nå nummer ${to + 1}.`);
  };

  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <Card
      title="Rekkefølge på TV"
      subtitle="Slik bytter TV-en mellom plakatene med «Vis på TV». Dra for å endre rekkefølgen."
      actions={
        <Select
          aria-label="Tid per plakat"
          fieldClassName={styles.seconds}
          value={slideSeconds}
          disabled={savingSeconds}
          onChange={(e) => onSlideSecondsChange(Number(e.target.value))}
        >
          {slideOptions(slideSeconds).map((s) => (
            <option key={s} value={s}>
              {s} s per plakat
            </option>
          ))}
        </Select>
      }
    >
      <div className={styles.layout}>
        {loading ? null : posters.length === 0 ? (
          <EmptyState icon="tv" title="Ingen plakater på TV">
            Slå på «Vis på TV» på en plakat for å vise den på TV-skjermen.
          </EmptyState>
        ) : (
          <ol className={styles.list} aria-label="Rekkefølge på TV">
            {posters.map((p, i) => (
              <li
                key={p.id}
                className={`${styles.item} ${
                  dragIndex === i ? styles.dragging : ""
                } ${
                  overIndex === i && dragIndex !== null && dragIndex !== i
                    ? dragIndex < i
                      ? styles.overBelow
                      : styles.overAbove
                    : ""
                }`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(p.id));
                  setDragIndex(i);
                }}
                onDragOver={(e) => {
                  if (dragIndex === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (overIndex !== i) setOverIndex(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) move(dragIndex, i);
                  endDrag();
                }}
                onDragEnd={endDrag}
              >
                <span className={styles.handle} aria-hidden="true">
                  ⋮⋮
                </span>
                <span className={styles.number}>{i + 1}</span>
                <span className={styles.thumb}>
                  {p.plakat && <img src={p.plakat} alt="" />}
                </span>
                <span className={styles.title}>{p.tittel}</span>
                <span className={styles.moves}>
                  <IconButton
                    size="sm"
                    label={`Flytt «${p.tittel}» opp`}
                    disabled={i === 0}
                    icon={
                      <span className={styles.rotate}>
                        <Icon name="chevronLeft" size={16} strokeWidth={2} />
                      </span>
                    }
                    onClick={() => move(i, i - 1)}
                  />
                  <IconButton
                    size="sm"
                    label={`Flytt «${p.tittel}» ned`}
                    disabled={i === posters.length - 1}
                    icon={
                      <span className={styles.rotate}>
                        <Icon name="chevronRight" size={16} strokeWidth={2} />
                      </span>
                    }
                    onClick={() => move(i, i + 1)}
                  />
                </span>
              </li>
            ))}
          </ol>
        )}
        <TvPreview posters={posters} slideSeconds={slideSeconds} />
      </div>
      <TvAlertSettings
        value={alertMinutes}
        onChange={onAlertMinutesChange}
        disabled={savingAlerts}
      />
      <span className={ui.srOnly} aria-live="polite">
        {announce}
      </span>
    </Card>
  );
};

export default TvOrderPanel;
